import { DepartmentTreeNode } from '@/interfaces/model/general/department';

/** ツリーを 1 行ずつ描画するために平坦化したノード。 */
export type FlatDepartmentRow = {
  node: DepartmentTreeNode;
  /** 0 始まりのインデント段数 (ルート = 0)。API の depth は 1 始まりなので別に持つ。 */
  indent: number;
  hasChildren: boolean;
};

/**
 * 折りたたみ状態を反映してツリーを平坦化する。
 *
 * 親が閉じている場合その子孫は行として出てこない。表示順は API が
 * display_order -> name -> uuid で整列済みなので、ここでは並べ替えない。
 */
export const flattenTree = (
  nodes: DepartmentTreeNode[],
  collapsedIds: ReadonlySet<string>,
  indent = 0
): FlatDepartmentRow[] => {
  const rows: FlatDepartmentRow[] = [];

  nodes.forEach((node) => {
    const hasChildren = node.children.length > 0;
    rows.push({ node, indent, hasChildren });

    if (hasChildren && !collapsedIds.has(node.uuid)) {
      rows.push(...flattenTree(node.children, collapsedIds, indent + 1));
    }
  });

  return rows;
};

/**
 * 自分自身 + 全子孫の uuid を集める。
 *
 * ツリーの「ユーザー数 / SDS数」は配下を含む集計なので、その数字から一覧へ
 * 遷移するときも配下を含めて絞り込まないと件数が一致しない。
 */
export const collectSubtreeIds = (node: DepartmentTreeNode): string[] => {
  const ids = [node.uuid];
  node.children.forEach((child) => {
    ids.push(...collectSubtreeIds(child));
  });
  return ids;
};

/**
 * ツリーを `scopeIds` を根とする部分木だけに絞り込む。
 *
 * 一致したノードは子孫をそのまま残す (配下は自動的に範囲内のため)。
 * 一致しないノードは除外するが、その配下に一致するノードがあれば、
 * それをルート直下へ繰り上げて残す。
 */
export const pruneTreeToScope = (
  nodes: DepartmentTreeNode[],
  scopeIds: ReadonlySet<string>
): DepartmentTreeNode[] =>
  nodes.flatMap((node) =>
    scopeIds.has(node.uuid) ? [node] : pruneTreeToScope(node.children, scopeIds)
  );

/** ツリー全体のノード数 (折りたたみ状態に関係なく数える)。 */
export const countNodes = (nodes: DepartmentTreeNode[]): number =>
  nodes.reduce((total, node) => total + 1 + countNodes(node.children), 0);

/** 子を持つノードの uuid をすべて集める (全展開 / 全折りたたみ用)。 */
export const collectParentIds = (nodes: DepartmentTreeNode[]): string[] => {
  const ids: string[] = [];
  nodes.forEach((node) => {
    if (node.children.length > 0) {
      ids.push(node.uuid);
      ids.push(...collectParentIds(node.children));
    }
  });
  return ids;
};

/**
 * 「本社 / 開発本部」のような祖先を含む表示名を作る。
 *
 * 親が違えば同名の部署を作れる仕様なので、セレクタや削除確認など
 * ツリーの文脈が無い場所では名前だけだと区別できない。
 */
export const buildPathLabels = (
  nodes: DepartmentTreeNode[],
  separator = ' / ',
  prefix = ''
): Array<{ uuid: string; label: string; depth: number }> => {
  const labels: Array<{ uuid: string; label: string; depth: number }> = [];

  nodes.forEach((node) => {
    const label = prefix ? `${prefix}${separator}${node.name}` : node.name;
    labels.push({ uuid: node.uuid, label, depth: node.depth });
    labels.push(...buildPathLabels(node.children, separator, label));
  });

  return labels;
};

/** ツリーから uuid でノードを探す。 */
export const findNode = (
  nodes: DepartmentTreeNode[],
  uuid: string
): DepartmentTreeNode | undefined => {
  for (const node of nodes) {
    if (node.uuid === uuid) {
      return node;
    }
    const found = findNode(node.children, uuid);
    if (found) {
      return found;
    }
  }
  return undefined;
};

/**
 * 親部署に選べないノードの uuid を集める。
 *
 * 自分自身と、その配下すべて。自分の配下に移すと循環するため
 * (DB 側のトリガでも弾かれるが、選ばせない方が親切)。
 */
export const collectInvalidParentIds = (
  node: DepartmentTreeNode
): Set<string> => new Set(collectSubtreeIds(node));

/**
 * 移動後に 10 階層を超えてしまう移動先を判定するため、部分木の高さを返す。
 * 自分だけなら 1、子が 1 段あれば 2。
 */
export const subtreeHeight = (node: DepartmentTreeNode): number =>
  node.children.length === 0
    ? 1
    : 1 + Math.max(...node.children.map(subtreeHeight));

/** 変更前 / 変更後プレビュー用に、部分木を罫線付きの行へ落とす。 */
export type PreviewLine = { uuid: string; text: string; isRoot: boolean };

export const buildSubtreePreview = (
  node: DepartmentTreeNode,
  parentName?: string
): PreviewLine[] => {
  const lines: PreviewLine[] = [];

  if (parentName) {
    lines.push({
      uuid: `__parent__${node.uuid}`,
      text: parentName,
      isRoot: true,
    });
    lines.push({ uuid: node.uuid, text: `└ ${node.name}`, isRoot: false });
  } else {
    lines.push({ uuid: node.uuid, text: node.name, isRoot: true });
  }

  const indent = parentName ? '　' : '';
  node.children.forEach((child, index) => {
    const isLast = index === node.children.length - 1;
    lines.push({
      uuid: child.uuid,
      text: `${indent}${isLast ? '└' : '├'} ${child.name}`,
      isRoot: false,
    });
  });

  return lines;
};

/** ルートから uuid までの祖先チェーン (自分自身を含む)。見つからなければ空。 */
export const collectAncestorChain = (
  nodes: DepartmentTreeNode[],
  uuid: string,
  chain: DepartmentTreeNode[] = []
): DepartmentTreeNode[] => {
  for (const node of nodes) {
    const next = [...chain, node];
    if (node.uuid === uuid) {
      return next;
    }
    const found = collectAncestorChain(node.children, uuid, next);
    if (found.length) {
      return found;
    }
  }
  return [];
};

/**
 * 新規作成時の「階層プレビュー」。
 * 親を選ぶとその祖先チェーンを上から並べ、末尾に作成される部署を置く。
 */
export const buildCreatePreview = (
  tree: DepartmentTreeNode[],
  parentId: string | null,
  name: string
): PreviewLine[] => {
  if (!parentId) {
    return [{ uuid: '__new__', text: name, isRoot: true }];
  }

  const chain = collectAncestorChain(tree, parentId);
  const lines: PreviewLine[] = chain.map((node, index) => ({
    uuid: node.uuid,
    text: index === 0 ? node.name : `${'　'.repeat(index - 1)}└ ${node.name}`,
    isRoot: index === 0,
  }));

  lines.push({
    uuid: '__new__',
    text: `${'　'.repeat(Math.max(chain.length - 1, 0))}└ ${name}`,
    isRoot: false,
  });

  return lines;
};

/** フラットな部署一覧から組み立てた、階層順に並んだ選択肢。 */
export type DepartmentOption = {
  uuid: string;
  name: string;
  /** 「本社 / 開発本部 / 開発部」。同名の部署を見分けるために使う。 */
  path: string;
  /** 0 始まりのインデント段数 (最上位 = 0)。 */
  indent: number;
};

type FlatDepartment = {
  uuid: string;
  name: string;
  parent_id?: string | null;
  display_order?: number;
};

/**
 * フラットな部署一覧を階層順に並べ、インデント段数とフルパスを付ける。
 *
 * 親が違えば同名の部署を作れるため、名前だけを並べたリストでは選びようがない。
 * ツリー API を叩き直さずに済むよう、一覧 API が返す parent_id から組み立てる。
 *
 * 一覧は閲覧権限で絞られることがあり、親が結果に含まれない子だけが来る場合が
 * ある。その子は最上位として扱う (握りつぶすと選択肢から消えてしまうため)。
 */
export const buildDepartmentOptions = (
  departments: readonly FlatDepartment[],
  separator = ' / '
): DepartmentOption[] => {
  const present = new Set(departments.map((d) => d.uuid));
  const childrenOf = new Map<string | null, FlatDepartment[]>();

  departments.forEach((department) => {
    // 親が一覧に無い (権限で見えない / 消えている) 場合は最上位として扱う
    const parentId =
      department.parent_id && present.has(department.parent_id)
        ? department.parent_id
        : null;
    const siblings = childrenOf.get(parentId);
    if (siblings) {
      siblings.push(department);
    } else {
      childrenOf.set(parentId, [department]);
    }
  });

  // API と同じ並び (display_order -> name -> uuid)。一覧 API が整列済みでない
  // 経路もあるので、ここでも揃えておく。
  childrenOf.forEach((siblings) => {
    siblings.sort(
      (a, b) =>
        (a.display_order ?? 0) - (b.display_order ?? 0) ||
        a.name.localeCompare(b.name) ||
        a.uuid.localeCompare(b.uuid)
    );
  });

  const options: DepartmentOption[] = [];
  const visited = new Set<string>();

  const walk = (parentId: string | null, indent: number, prefix: string) => {
    const siblings = childrenOf.get(parentId) ?? [];

    siblings.forEach((department) => {
      // データ不整合で循環していても無限再帰にしない
      if (visited.has(department.uuid)) {
        return;
      }
      visited.add(department.uuid);

      const path = prefix
        ? `${prefix}${separator}${department.name}`
        : department.name;
      options.push({
        uuid: department.uuid,
        name: department.name,
        path,
        indent,
      });
      walk(department.uuid, indent + 1, path);
    });
  };

  walk(null, 0, '');

  return options;
};

/**
 * SelectInTextField 用の部署選択肢。
 *
 * title はフルパスにする。SelectInTextField の絞り込みとチップ表示が title を
 * 見るため、ここを名前だけにすると同名の部署を選び分けられない。
 * ドロップダウンの行は tree を使って罫線付きで描く。
 */
export const buildDepartmentSelectOptions = (
  departments: readonly FlatDepartment[]
): Array<{ value: string; title: string; tree: DepartmentOption }> =>
  buildDepartmentOptions(departments).map((option) => ({
    value: option.uuid,
    title: option.path,
    tree: option,
  }));

/**
 * uuid -> フルパス の対応表。
 *
 * 1 階層上の親名だけでは区別できない (3 階層以上で親も同名になりうる)。
 * 同名の部署をツリーの文脈が無い場所 (SDS 詳細・一覧、ユーザー一覧など) で
 * 見分けるには、常にルートからのフルパスで表示する必要がある。
 */
export const buildDepartmentPathMap = (
  departments: readonly FlatDepartment[]
): Map<string, string> =>
  new Map(
    buildDepartmentOptions(departments).map((option) => [
      option.uuid,
      option.path,
    ])
  );
